import { useMutation, useQuery } from '@tanstack/react-query';
import { useAuthContext } from '../../contexts/AuthContext';
import { useAuth } from '../../contexts/auth/useAuth';
import { queryClient } from './react-query';
import { useApi } from './useApi';
import { User } from '../../types/Users.types';
import { AccountResponse } from '../../types/Account.types';

type AddUserArgs = {
  data: {
    email: string;
  };
};

export const useUsers = () => {
  const { makeApiRequest } = useApi();
  const { account } = useAuthContext();
  // AuthContextProvider also wraps /welcome, where nobody is signed in yet.
  const { isAuthenticated } = useAuth();
  const { data: users, isLoading: isLoadingUsers } = useQuery({
    queryKey: ['users', account],
    queryFn: () => makeApiRequest<User[]>('/users', 'GET'),
    enabled: isAuthenticated,
  });

  // The list comes from Auth0's user search, which takes a few seconds to
  // catch up with a change, so refetching straight after one returns the old
  // list. Once the API confirms the change, the cached list is updated
  // directly instead; the next normal refetch finds Auth0 caught up.
  const { mutateAsync: deleteUserMutation } = useMutation({
    mutationFn: async (email: string) => {
      await makeApiRequest(`/users/${email}`, 'DELETE');
    },
    onSuccess: (_, email) => {
      queryClient.setQueryData<User[]>(['users', account], (current) =>
        current?.filter((user) => user.email !== email)
      );
    },
  });

  const { mutateAsync: addUserMutation } = useMutation({
    mutationFn: ({ data }: AddUserArgs) =>
      makeApiRequest<User>('/users', 'POST', data),
    onSuccess: (user) => {
      queryClient.setQueryData<User[]>(['users', account], (current) =>
        current && user ? [...current, user] : current
      );
    },
  });

  const { data: accountData, isLoading: isLoadingAccount } = useQuery({
    queryKey: ['account'],
    queryFn: () => makeApiRequest<AccountResponse>('/accounts', 'GET'),
    enabled: isAuthenticated,
  });

  return {
    users,
    isLoadingUsers,
    deleteUser: deleteUserMutation,
    addUser: addUserMutation,
    accounts: accountData || {},
    isLoadingAccount,
  };
};
